import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Box, alpha, Tooltip, useTheme } from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import {
  WorkflowStepKind,
  WorkflowDashboardStepDto,
  WorkflowStepInstanceStatus,
} from 'src/@types/workflow/types';

import { STEP_KINDS, StepKindIcon } from 'src/sections/shared/workflow/stepKind';

const MARKER_SIZE = 28;
const MARKER_ICON_SIZE = 16;
const PENDING_OPACITY = 0.35;

const STATUS_LABEL_KEYS_IN_TITLE: Partial<Record<WorkflowStepInstanceStatus, string>> = {
  [WorkflowStepInstanceStatus.Rejected]: 'workflows.status.rejected',
  [WorkflowStepInstanceStatus.Cancelled]: 'workflows.status.cancelled',
};

type DashboardWorkflowRunStepMarkerProps = {
  step: WorkflowDashboardStepDto;
  kind: WorkflowStepKind;
};

export default function DashboardWorkflowRunStepMarker({
  step,
  kind,
}: DashboardWorkflowRunStepMarkerProps) {
  const theme = useTheme();
  const { t } = useLocales();

  const isRejected = step.status === WorkflowStepInstanceStatus.Rejected;
  const isActive = step.status === WorkflowStepInstanceStatus.Active;
  const hasFinished = isRejected || step.status === WorkflowStepInstanceStatus.Completed;
  const palette = isRejected ? 'error' : 'primary';

  const stepKindLabel = t(STEP_KINDS[kind].labelKey);
  const stepLabel = step.assigneeEmail ? `${stepKindLabel} — ${step.assigneeEmail}` : stepKindLabel;
  const statusLabelKey = STATUS_LABEL_KEYS_IN_TITLE[step.status];
  const title = statusLabelKey ? `${stepLabel} — ${t(statusLabelKey)}` : stepLabel;

  return (
    <Tooltip title={title}>
      <Box
        sx={{
          width: MARKER_SIZE,
          height: MARKER_SIZE,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: `${palette}.main`,
          bgcolor: hasFinished ? `${palette}.main` : 'transparent',
          color: hasFinished ? `${palette}.contrastText` : `${palette}.main`,
          opacity: hasFinished || isActive ? 1 : PENDING_OPACITY,
          boxShadow: isActive ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}` : 'none',
        }}
      >
        {isRejected ? (
          <CloseIcon sx={{ fontSize: MARKER_ICON_SIZE }} />
        ) : step.status === WorkflowStepInstanceStatus.Completed ? (
          <CheckIcon sx={{ fontSize: MARKER_ICON_SIZE }} />
        ) : (
          <StepKindIcon kind={kind} sx={{ fontSize: MARKER_ICON_SIZE }} />
        )}
      </Box>
    </Tooltip>
  );
}
